
import SelectDayTemplate from '@/components/select-day-template.vue'
import DayTemplateForm from '@/components/day-template-form.vue'
import { computed, defineComponent, PropType, ref, watch } from 'vue'
import { MyWorkTab } from '@/interfaces'
import { DayDto, UnavailableDaysDto } from 'service/__generated-api'
import { parse as parseDate } from 'date-fns'
import { notAvailableArgs } from '@/utils'
import { useStore } from 'vuex'

export default defineComponent({
  components: { SelectDayTemplate, DayTemplateForm },
  props: {
    activeTab: {
      required: true,
      type: String as PropType<MyWorkTab>
    },
    selectedDay: {
      required: true,
      type: Object as PropType<DayDto>
    },
    unavailableDays: {
      required: false,
      type: Object as PropType<UnavailableDaysDto>
    },
    selectedDays: {
      required: true,
      type: Array as PropType<string[]>
    }
  },
  emits: [
    'active-tab-change',
    'remove-selection',
    'apply-to-selection',
    'apply-holiday-to-selection',
    'apply-not-available-to-selection'
  ],
  setup(props, { emit }) {
    const store = useStore()
    const timesInvalidity = ref(false)
    const comment = ref('')
    const halfNotAvailable = ref(false)
    const markedByPartTimeUser = ref(false)
    const halfNotAvailableStartTime = ref('')
    const halfNotAvailableEndTime = ref('')

    const absenceOptions = [
      { key: 'notAvailable', name: 'Absent' },
      { key: 'unpaid', name: 'Unpaid absence' },
      { key: 'occasionalAbsence', name: 'Special absence' },
      { key: 'atConference', name: 'Course/Conference' }
    ] as const

    type AbsenceOptionKey = typeof absenceOptions[number]['key']

    const usedSpecialAbsenceDays = computed(() => {
      const occasional = props.unavailableDays?.occasional
      if (!occasional) {
        return 0
      }
      return occasional.accepted + occasional.planned
    })

    const specialAbsenceLimitReached = computed(
      () => usedSpecialAbsenceDays.value >= 2
    )

    const usedPaidLeaveDays = computed(() => {
      const paid = props.unavailableDays?.paid
      if (!paid) {
        return 0
      }
      return paid.currentYearUsed
    })

    const paidLeaveLimit = computed(
      () => props.unavailableDays?.paid.baseLimit ?? 30
    )

    const paidLeaveLimitReached = computed(
      () => usedPaidLeaveDays.value >= paidLeaveLimit.value
    )

    const januaryCarryoverAvailable = computed(
      () => (props.unavailableDays?.paid.carriedOver ?? 0) > 0
    )

    const paidLeaveInfoMessage = computed(() => {
      const paid = props.unavailableDays?.paid

      if (!paid) {
        return 'You have already used 30 days of paid leave this year.'
      }

      if (paid.carriedOver > 0) {
        return `You have already used all ${paid.effectiveLimit} paid leave days available in January, including ${paid.carriedOver} carried over from the previous year.`
      }

      return `You have already used ${paid.baseLimit} days of paid leave this year.`
    })

    const isSelectedDayPaidLeave = computed(
      () =>
        props.selectedDay.notAvailable &&
        !props.selectedDay.unpaid &&
        !props.selectedDay.occasionalAbsence &&
        !props.selectedDay.atConference &&
        !props.selectedDay.markedByPartTimeUser
    )

    function getDefaultAbsenceType(): AbsenceOptionKey {
      return paidLeaveLimitReached.value ? 'unpaid' : 'notAvailable'
    }

    function getAbsenceTypeFromDay(day: DayDto): AbsenceOptionKey {
      if (day.unpaid) {
        return 'unpaid'
      }
      if (day.occasionalAbsence) {
        return 'occasionalAbsence'
      }
      if (day.atConference) {
        return 'atConference'
      }
      if (day.notAvailable) {
        return 'notAvailable'
      }
      return getDefaultAbsenceType()
    }

    const absenceType = ref<AbsenceOptionKey>(getDefaultAbsenceType())

    const notAvailable = computed(() => absenceType.value === 'notAvailable')
    const unpaid = computed(() => absenceType.value === 'unpaid')
    const occasionalAbsence = computed(
      () => absenceType.value === 'occasionalAbsence'
    )
    const atConference = computed(() => absenceType.value === 'atConference')

    function syncSelectedDayState(day: DayDto) {
      comment.value = day.comment || ''
      halfNotAvailable.value = day.halfNotAvailable || false
      markedByPartTimeUser.value = day.markedByPartTimeUser || false
      halfNotAvailableStartTime.value = day.halfNotAvailableStartTime || ''
      halfNotAvailableEndTime.value = day.halfNotAvailableEndTime || ''
      absenceType.value = getAbsenceTypeFromDay(day)
    }

    watch(
      () => props.selectedDay,
      day => syncSelectedDayState(day),
      { deep: true, immediate: true }
    )

    const isUserPartTimeEmployee = computed(
      () => store.state.user.worksPartTime
    )

    const notAvailableIndicators = computed<notAvailableArgs>(() => ({
      comment: comment.value,
      halfNotAvailable: halfNotAvailable.value,
      atConference: atConference.value,
      unpaid: unpaid.value,
      occasionalAbsence: occasionalAbsence.value,
      notAvailable: notAvailable.value,
      halfNotAvailableStartTime: halfNotAvailableStartTime.value,
      halfNotAvailableEndTime: halfNotAvailableEndTime.value,
      markedByPartTimeUser: markedByPartTimeUser.value
    }))

    const commentPlaceholderText = computed(() => {
      if (occasionalAbsence.value) {
        return 'e.g. Odebranie nadgodzin 🕑'
      }
      if (unpaid.value) {
        return 'e.g. Nieplanowany wyjazd ❌'
      }
      if (atConference.value) {
        return 'e.g. Konferencja 🎓'
      }
      return 'e.g. Urlop / Odrobienie nieobecności ❌'
    })

    const haveToFillTimeWhenHalfAbsence = computed(
      () =>
        halfNotAvailable.value &&
        !halfNotAvailableStartTime.value &&
        !halfNotAvailableEndTime.value
    )

    function setAbsenceType(value: AbsenceOptionKey) {
      absenceType.value = value
      halfNotAvailable.value = false
      halfNotAvailableStartTime.value = ''
      halfNotAvailableEndTime.value = ''
      markedByPartTimeUser.value = false
    }

    const selectedAbsenceType = computed({
      get() {
        return absenceType.value
      },
      set(value: AbsenceOptionKey) {
        setAbsenceType(value)
      }
    })

    const finishMarkingUnavailabilityText = computed(() => {
      if (absenceType.value === 'occasionalAbsence') return 'Special absence'
      if (absenceType.value === 'atConference')
        return 'Going on course or conference'
      if (absenceType.value === 'unpaid') return 'Unpaid absence'
      return 'Not available'
    })

    function checkTimesAreValidAndSet4hStep() {
      const formatRegex = /^\d\d:\d\d$/
      if (!formatRegex.test(halfNotAvailableStartTime.value)) {
        timesInvalidity.value = true
      } else {
        timesInvalidity.value = !formatRegex.test(halfNotAvailableEndTime.value)
      }
      if (!timesInvalidity.value) set4hTimeStep()
    }

    function set4hTimeStep() {
      const fromTime = parseDate(
        halfNotAvailableStartTime.value,
        'HH:mm',
        new Date()
      )
      const limitedToTimeByMax4hStepPerDay = parseDate(
        '20:00',
        'HH:mm',
        new Date()
      )
      if (!fromTime.getTime() || fromTime > limitedToTimeByMax4hStepPerDay) {
        timesInvalidity.value = true
      } else {
        const dateToStr = (date: Date) =>
          date.toTimeString().split(' ')[0].slice(0, -3)
        const addHours = (date: Date, hours: number) => {
          date.setHours(date.getHours() + hours)
          return date
        }
        const newEndTimeWithAdded4h = addHours(fromTime, 4)
        halfNotAvailableEndTime.value = dateToStr(newEndTimeWithAdded4h)
      }
    }

    function setDefaultTime() {
      halfNotAvailableStartTime.value = halfNotAvailableStartTime.value
        ? ''
        : '10:00'
      halfNotAvailableEndTime.value = halfNotAvailableEndTime.value
        ? ''
        : '14:00'
    }

    function adjustChecksDueToPartTimeJob() {
      if (markedByPartTimeUser.value) {
        halfNotAvailable.value = false
        halfNotAvailableStartTime.value = ''
        halfNotAvailableEndTime.value = ''
      }
    }

    function isAbsenceOptionDisabled(optionKey: string) {
      return (
        (optionKey === 'occasionalAbsence' &&
          specialAbsenceLimitReached.value &&
          !props.selectedDay.occasionalAbsence) ||
        (optionKey === 'notAvailable' &&
          paidLeaveLimitReached.value &&
          !isSelectedDayPaidLeave.value)
      )
    }

    return {
      MyWorkTab,
      emit,
      timesInvalidity,
      comment,
      halfNotAvailable,
      markedByPartTimeUser,
      atConference,
      unpaid,
      occasionalAbsence,
      notAvailable,
      halfNotAvailableStartTime,
      halfNotAvailableEndTime,
      isUserPartTimeEmployee,
      notAvailableIndicators,
      commentPlaceholderText,
      haveToFillTimeWhenHalfAbsence,
      selectedAbsenceType,
      absenceOptions,
      specialAbsenceLimitReached,
      paidLeaveLimitReached,
      januaryCarryoverAvailable,
      paidLeaveInfoMessage,
      isSelectedDayPaidLeave,
      finishMarkingUnavailabilityText,
      checkTimesAreValidAndSet4hStep,
      setDefaultTime,
      adjustChecksDueToPartTimeJob,
      isAbsenceOptionDisabled
    }
  }
})
